import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import styles from './ResultSyncFailureBanner.module.css';
import { getResultSyncStatus } from 'api/resultApi';
import routes from 'routes';

/**
 * Failure-only safety net for the results sync queue on the meet details page
 * (docs/results-sync-queue-design.md in the portal repo). One silent status
 * check on mount; renders nothing unless failed sync jobs exist, then shows a
 * plain-English warning linking to the Results Sync report, which holds the
 * detail and the Retry action.
 *
 * The sync-status endpoint is admin-only. The caller must gate rendering on the
 * user's permission level (members heavily view the meet page) — this component
 * fetches on mount, so mounting it for a non-admin would fire a 403 per view.
 */
const ResultSyncFailureBanner = ({ meetId }) => {
  const [failedCount, setFailedCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const checkStatus = async () => {
      try {
        const response = await getResultSyncStatus(meetId);
        if (!cancelled) {
          setFailedCount(response.data?.failed || 0);
        }
      } catch (err) {
        // Silent by design — a failed diagnostic check must not disturb the page.
        console.log('[ResultSyncFailureBanner] Status check failed:', err);
      }
    };
    checkStatus();
    return () => {
      cancelled = true;
    };
  }, [meetId]);

  if (failedCount === 0) return null;

  return (
    <div className={styles.banner}>
      <AlertTriangle size={16} className={styles.icon} />
      <span>
        {failedCount === 1
          ? 'A results upload for this meet failed to sync.'
          : `${failedCount} results uploads for this meet failed to sync.`}{' '}
        Open the{' '}
        <Link to={routes.admin.reports.resultsSync} className={styles.link}>
          Results Sync report
        </Link>{' '}
        to review and retry.
      </span>
    </div>
  );
};

ResultSyncFailureBanner.propTypes = {
  meetId: PropTypes.string.isRequired,
};

export default ResultSyncFailureBanner;
